import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiPaths } from "../../Config/ApiPath";
import useAxiosHelper from "./../../Common/AxiosHelper";
import "./GenealogyTree.css";
import moment from "moment";
import {
  Network,
  X,
  Info,
  Search,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Crosshair,
  Users,
  UserCheck,
  UserX,
  GitBranch,
  Layers,
  RotateCcw,
  Home,
} from "lucide-react";
import { BasicInfo } from "../../Config/BasicInfo";

// Siblings rendered per batch — keeps very wide levels smooth
const SIBLING_PAGE = 30;
// Levels expanded when the tree first loads
const INITIAL_DEPTH = 1;

/* ---------- tree helpers ---------- */

// One pass over the API tree: depth, parent, team size and active counts per
// node, plus a flat list for search. The API returns the whole downline.
function indexTree(root) {
  const byId = new Map();
  const flat = [];
  let maxDepth = 0;
  const walk = (node, depth, parentId) => {
    const kids = Array.isArray(node.children) ? node.children : [];
    let team = 0;
    let active = 0;
    for (const child of kids) {
      const c = walk(child, depth + 1, node.id);
      team += 1 + c.team;
      active += (c.isActive ? 1 : 0) + c.active;
    }
    const isActive = !!node?.attributes?.status;
    const info = {
      node,
      id: node.id,
      depth,
      parentId,
      team,
      active,
      isActive,
      username: node?.attributes?.Username || "",
      name: node?.attributes?.Name || "",
    };
    byId.set(node.id, info);
    flat.push(info);
    if (depth > maxDepth) maxDepth = depth;
    return info;
  };
  if (root) walk(root, 0, null);
  return { byId, flat, maxDepth };
}

// Ancestors of `id` (root first, excluding `id`)
function ancestorsOf(byId, id) {
  const out = [];
  let cur = byId.get(id)?.parentId;
  while (cur !== null && cur !== undefined) {
    out.unshift(cur);
    cur = byId.get(cur)?.parentId;
  }
  return out;
}

const initials = (name, username) =>
  (name || username || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");

function Highlight({ text, query }) {
  if (!query) return text;
  const i = text.toLowerCase().indexOf(query.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + query.length)}</mark>
      {text.slice(i + query.length)}
    </>
  );
}

export default function Genealogy() {
  const [orgChart, setOrgChart] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [popupDataLoading, setPopupDataLoading] = useState(false);
  const [show, setShow] = useState(false);
  const { AxiosPost } = useAxiosHelper();
  const [companyData, setCompanyData] = useState([]);
  const [data, setData] = useState();

  // tree view state
  const [expanded, setExpanded] = useState(() => new Set());
  const [focusId, setFocusId] = useState(null); // re-rooted view
  const [selectedId, setSelectedId] = useState(null);
  const [detailId, setDetailId] = useState(null); // member shown in the sheet
  const [visibleCount, setVisibleCount] = useState({}); // id -> siblings shown
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const rowRefs = useRef(new Map());
  const searchRef = useRef(null);

  const handleClose = () => setShow(false);

  async function CompanyInfo() {
    try {
      const raw = localStorage.getItem("companyData");
      if (raw) setCompanyData(JSON.parse(raw));
    } catch (error) {
      BasicInfo.isDebug && console.log(error);
    }
  }

  var x = 0;
  useEffect(() => {
    if (x === 0) {
      getData();
      CompanyInfo();
      x++;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getData = async () => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) throw new Error("User ID not found in localStorage");
      const response = await AxiosPost(ApiPaths.generationTree, { uid: userId });
      if (response) setOrgChart(response);
    } catch (error) {
      console.error("Error fetching tree data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Dialog behaviour: Escape closes it, and the page behind stays put instead
  // of scrolling under the overlay while the sheet is open.
  useEffect(() => {
    if (!show) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShow(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [show]);

  const getPopupData = async (uid) => {
    try {
      setShow(true);
      setPopupDataLoading(true);
      setData(null);
      const response = await AxiosPost(ApiPaths.getProfileUsername, { uid });
      setData(response);
    } catch (error) {
      console.error("Error fetching popup data:", error);
    } finally {
      setPopupDataLoading(false);
    }
  };

  const fmtNum = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return v ?? "0";
    return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  };
  const fmtDate = (d) => (d ? moment(d).format("lll") : null);

  /* ---------- derived tree data ---------- */
  const index = useMemo(() => indexTree(orgChart), [orgChart]);
  const rootInfo = orgChart ? index.byId.get(orgChart.id) : null;
  const viewRoot = (focusId !== null && index.byId.get(focusId)) || rootInfo;

  const expandToDepth = useCallback(
    (depth, base = viewRoot) => {
      if (!base) return;
      const next = new Set();
      const limit = base.depth + depth;
      for (const info of index.flat) {
        if (info.depth < limit && info.node.children?.length) next.add(info.id);
      }
      setExpanded(next);
    },
    [index, viewRoot]
  );

  // Initial expansion whenever a new tree loads
  useEffect(() => {
    if (!rootInfo) return;
    setFocusId(null);
    setSelectedId(null);
    setVisibleCount({});
    const next = new Set();
    for (const info of index.flat) {
      if (info.depth < INITIAL_DEPTH && info.node.children?.length) next.add(info.id);
    }
    setExpanded(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Reveal a member: open every ancestor, select it, scroll it into view.
  // If it sits outside the focused branch, fall back to the full tree.
  const reveal = (id) => {
    const anc = ancestorsOf(index.byId, id);
    if (viewRoot && id !== viewRoot.id && !anc.includes(viewRoot.id)) setFocusId(null);
    setExpanded((prev) => {
      const next = new Set(prev);
      anc.forEach((a) => next.add(a));
      return next;
    });
    // make sure paging doesn't hide it among many siblings
    const info = index.byId.get(id);
    const parent = info && index.byId.get(info.parentId);
    if (parent) {
      const pos = parent.node.children.findIndex((c) => c.id === id);
      setVisibleCount((vc) => ({ ...vc, [parent.id]: Math.max(vc[parent.id] || SIBLING_PAGE, pos + 1) }));
    }
    setSelectedId(id);
    setQuery("");
    setSearchOpen(false);
    requestAnimationFrame(() =>
      setTimeout(() => rowRefs.current.get(id)?.scrollIntoView({ behavior: "smooth", block: "center" }), 60)
    );
  };

  const focusOn = (id) => {
    setFocusId(id === rootInfo?.id ? null : id);
    setSelectedId(id);
    setExpanded((prev) => new Set(prev).add(id));
  };

  const breadcrumb = viewRoot && rootInfo && viewRoot.id !== rootInfo.id
    ? [...ancestorsOf(index.byId, viewRoot.id), viewRoot.id].map((id) => index.byId.get(id))
    : [];

  // ancestors of the selected member — their connectors are highlighted
  const pathSet = useMemo(() => {
    if (selectedId === null) return new Set();
    return new Set([...ancestorsOf(index.byId, selectedId), selectedId]);
  }, [index, selectedId]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return index.flat
      .filter((i) => i.username.toLowerCase().includes(q) || i.name.toLowerCase().includes(q))
      .sort((a, b) => a.depth - b.depth)
      .slice(0, 8);
  }, [index, query]);

  // close search results on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const stats = rootInfo
    ? [
        { label: "Total team", value: rootInfo.team, Icon: Users, tone: "orange" },
        { label: "Active", value: rootInfo.active, Icon: UserCheck, tone: "mint" },
        { label: "Inactive", value: rootInfo.team - rootInfo.active, Icon: UserX, tone: "rose" },
        { label: "Direct", value: rootInfo.node.children?.length || 0, Icon: GitBranch, tone: "blue" },
        { label: "Levels deep", value: index.maxDepth, Icon: Layers, tone: "violet" },
      ]
    : [];

  /* ---------- rendering ---------- */
  const renderNode = (info, isLast) => {
    const kids = info.node.children || [];
    const open = expanded.has(info.id);
    const onPath = pathSet.has(info.id);
    const selected = selectedId === info.id;
    const shown = visibleCount[info.id] || SIBLING_PAGE;
    const level = info.depth - (rootInfo?.depth || 0);

    return (
      <li
        key={info.id}
        className={`gtItem ${isLast ? "isLast" : ""} ${onPath ? "onPath" : ""}`}
      >
        <div
          ref={(el) => (el ? rowRefs.current.set(info.id, el) : rowRefs.current.delete(info.id))}
          className={`gtRow ${selected ? "isSelected" : ""} ${info.isActive ? "isActive" : "isInactive"}`}
          onClick={() => setSelectedId(info.id)}
        >
          <button
            type="button"
            className={`gtToggle ${kids.length ? "" : "isLeaf"} ${open ? "isOpen" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              if (kids.length) toggle(info.id);
            }}
            disabled={!kids.length}
            aria-expanded={kids.length ? open : undefined}
            aria-label={kids.length ? `${open ? "Collapse" : "Expand"} ${info.username}` : undefined}
          >
            {kids.length ? <span className="gtToggleIcon" aria-hidden="true" /> : <span className="gtLeafDot" />}
          </button>

          <span className="gtAvatar" aria-hidden="true">
            {initials(info.name, info.username)}
            <i className="gtStatusDot" />
          </span>

          <span className="gtMain">
            <span className="gtName">
              <Highlight text={info.name || info.username} query={query.trim()} />
            </span>
            <span className="gtMeta">
              <span className="gtUser">@{info.username}</span>
              <span className={`gtLevel lv${Math.min(level, 6)}`}>
                {level === 0 ? "You" : `L${level}`}
              </span>
            </span>
          </span>

          <span className="gtSide">
            {kids.length > 0 && (
              <span className="gtCount" title={`${kids.length} direct · ${info.team} total`}>
                <Users size={12} />
                {kids.length}
                {info.team > kids.length && <em>/{info.team}</em>}
              </span>
            )}
            <span className="gtActions">
              {kids.length > 0 && info.id !== viewRoot?.id && (
                <button
                  type="button"
                  className="gtAct isFocus"
                  title="Focus on this branch"
                  aria-label={`Focus on ${info.username}'s branch`}
                  onClick={(e) => {
                    e.stopPropagation();
                    focusOn(info.id);
                  }}
                >
                  <Crosshair size={15} />
                </button>
              )}
              <button
                type="button"
                className="gtAct"
                title="View details"
                aria-label={`View ${info.username} details`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedId(info.id);
                  setDetailId(info.id);
                  getPopupData(info.username);
                }}
              >
                <Info size={15} />
              </button>
            </span>
          </span>
        </div>

        {open && kids.length > 0 && (
          <ul className="gtChildren">
            {kids.slice(0, shown).map((child, i, arr) =>
              renderNode(index.byId.get(child.id), i === arr.length - 1 && shown >= kids.length)
            )}
            {kids.length > shown && (
              <li className="gtItem isLast gtMoreItem">
                <button
                  type="button"
                  className="gtMore"
                  onClick={() => setVisibleCount((vc) => ({ ...vc, [info.id]: shown + SIBLING_PAGE }))}
                >
                  Show {Math.min(SIBLING_PAGE, kids.length - shown)} more
                  <span>of {kids.length - shown} remaining</span>
                </button>
              </li>
            )}
          </ul>
        )}
      </li>
    );
  };

  const active = data?.status == "1";
  const allExpanded = rootInfo && index.flat.every((i) => !i.node.children?.length || expanded.has(i.id));

  return (
    <section className="gtPage">
      {/* ---------- Header ---------- */}
      <header className="gtHeader">
        <div className="gtHeaderText">
          <span className="gtEyebrow">
            <Network size={14} /> Network
          </span>
          <h1>Genealogy</h1>
          <p>Your complete downline, level by level. Expand a branch, focus on it, or search for any member.</p>
        </div>
      </header>

      {/* ---------- Stats ---------- */}
      {rootInfo && (
        <div className="gtStats">
          {stats.map(({ label, value, Icon, tone }) => (
            <div key={label} className={`gtStat tone-${tone}`}>
              <span className="gtStatIcon">
                <Icon size={17} />
              </span>
              <span className="gtStatValue">{fmtNum(value)}</span>
              <span className="gtStatLabel">{label}</span>
            </div>
          ))}
        </div>
      )}

      {/* ---------- Tree panel ---------- */}
      <div className="gtPanel">
        {rootInfo && (
          <div className="gtToolbar">
            <div className="gtSearch" ref={searchRef}>
              <Search size={17} className="gtSearchIcon" />
              <input
                type="search"
                placeholder="Search member by name or username"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && results[0]) reveal(results[0].id);
                  if (e.key === "Escape") setSearchOpen(false);
                }}
                autoCapitalize="none"
                autoCorrect="off"
                aria-label="Search members"
              />
              {query && (
                <button type="button" className="gtSearchClear" onClick={() => setQuery("")} aria-label="Clear search">
                  <X size={15} />
                </button>
              )}
              {searchOpen && query.trim() && (
                <div className="gtResults" role="listbox">
                  {results.length ? (
                    results.map((r) => (
                      <button key={r.id} type="button" className="gtResult" onClick={() => reveal(r.id)}>
                        <span className={`gtAvatar sm ${r.isActive ? "" : "off"}`}>
                          {initials(r.name, r.username)}
                        </span>
                        <span className="gtResultText">
                          <b>
                            <Highlight text={r.name || r.username} query={query.trim()} />
                          </b>
                          <small>
                            @<Highlight text={r.username} query={query.trim()} /> · Level {r.depth}
                          </small>
                        </span>
                        <ChevronRight size={16} />
                      </button>
                    ))
                  ) : (
                    <div className="gtNoResult">No member matches “{query.trim()}”</div>
                  )}
                </div>
              )}
            </div>

            <div className="gtControls">
              <div className="gtSegment" role="group" aria-label="Expand to level">
                {[1, 2, 3].map((d) => (
                  <button key={d} type="button" onClick={() => expandToDepth(d)}>
                    L{d}
                  </button>
                ))}
                <button type="button" onClick={() => expandToDepth(Infinity)}>
                  All
                </button>
              </div>
              <button
                type="button"
                className="gtIconBtn"
                onClick={() => (allExpanded ? setExpanded(new Set([viewRoot.id])) : expandToDepth(Infinity))}
                title={allExpanded ? "Collapse all" : "Expand all"}
                aria-label={allExpanded ? "Collapse all" : "Expand all"}
              >
                {allExpanded ? <ChevronsDownUp size={18} /> : <ChevronsUpDown size={18} />}
              </button>
            </div>
          </div>
        )}

        {breadcrumb.length > 0 && (
          <nav className="gtCrumbs" aria-label="Focused branch">
            <button type="button" className="gtCrumb home" onClick={() => focusOn(rootInfo.id)}>
              <Home size={14} /> You
            </button>
            {breadcrumb.slice(1).map((b) => (
              <React.Fragment key={b.id}>
                <ChevronRight size={14} className="gtCrumbSep" />
                <button
                  type="button"
                  className={`gtCrumb ${b.id === viewRoot.id ? "isCurrent" : ""}`}
                  onClick={() => focusOn(b.id)}
                >
                  {b.username}
                </button>
              </React.Fragment>
            ))}
            <button type="button" className="gtCrumbReset" onClick={() => focusOn(rootInfo.id)}>
              <RotateCcw size={13} /> Full tree
            </button>
          </nav>
        )}

        <div className="gtCanvas">
          {isLoading && (
            <div className="gtState">
              <div className="gtSpinner" />
              <span>Loading your network…</span>
            </div>
          )}
          {!isLoading && !orgChart && (
            <div className="gtState">
              <span className="gtStateIcon">
                <Network size={30} strokeWidth={1.5} />
              </span>
              <b>No network yet</b>
              <span>Members you refer will appear here as your tree grows.</span>
            </div>
          )}
          {!isLoading && viewRoot && (
            <ul className="gtTree" key={viewRoot.id}>
              {renderNode(viewRoot, true)}
            </ul>
          )}
        </div>

        {rootInfo && (
          <div className="gtLegend">
            <span><i className="gtLegendDot on" /> Active</span>
            <span><i className="gtLegendDot off" /> Inactive</span>
            <span><Users size={12} /> direct / total team</span>
            <span><Crosshair size={12} /> focus branch</span>
          </div>
        )}
      </div>

      {/* ---------- Member detail sheet ---------- */}
      {show && (
        <div className="gtModalOverlay" onClick={handleClose}>
          <div
            className="gtModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="gtModalTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="gtModalHandle" aria-hidden="true" />
            <button className="gtModalClose" onClick={handleClose} aria-label="Close">
              <X size={18} />
            </button>

            {popupDataLoading ? (
              <div className="gtModalState">
                <div className="gtSpinner" />
                <span>Loading details…</span>
              </div>
            ) : data ? (
              <>
                <div className="gtModalHead">
                  <div className={`gtModalAvatar ${active ? "on" : "off"}`}>
                    {initials(data?.name, data?.username)}
                  </div>
                  <div className="gtModalHeadInfo">
                    <h3 id="gtModalTitle">{data?.name || "—"}</h3>
                    <p>@{data?.username}</p>
                    <span className={`gtBadge ${active ? "on" : "off"}`}>
                      <i /> {active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                <div className="gtModalStats">
                  <div>
                    <span>Direct Team</span>
                    <b>{fmtNum(data?.DirectTeam)}</b>
                  </div>
                  <div>
                    <span>Self Package</span>
                    <b>${fmtNum(data?.selfInvestment)}</b>
                  </div>
                  <div>
                    <span>Direct Business</span>
                    <b>${fmtNum(data?.directBusiness)}</b>
                  </div>
                  <div>
                    <span>Team Business</span>
                    <b>${fmtNum(data?.TeamBusiness)}</b>
                  </div>
                </div>

                {(() => {
                  const d = index.byId.get(detailId);
                  if (!d || !d.node.children?.length || d.id === viewRoot?.id) return null;
                  return (
                    <button
                      type="button"
                      className="gtModalFocus"
                      onClick={() => {
                        focusOn(d.id);
                        setShow(false);
                      }}
                    >
                      <Crosshair size={17} />
                      Focus on this branch
                      <span>{d.team} member{d.team === 1 ? "" : "s"}</span>
                    </button>
                  );
                })()}

                <div className="gtModalMeta">
                  <div>
                    <span>Activation Date</span>
                    <b className={fmtDate(data?.Activation_date) ? "" : "muted"}>
                      {fmtDate(data?.Activation_date) || "Not activated"}
                    </b>
                  </div>
                  <div>
                    <span>Joining Date</span>
                    <b>{fmtDate(data?.joining_date) || "—"}</b>
                  </div>
                </div>
              </>
            ) : (
              <div className="gtModalState">
                <Info size={30} />
                <span>No data available</span>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
